import React, { useEffect, useState } from "react";
import styles from "./UtilityBar.module.scss";
import SearchIcon from "../../../assets/images/inputSearchSuffix.svg";
import CalendarIcon from "../../../assets/images/calendar-filled.svg";
import ListViewIcon from "../../../assets/images/listViewIcon.svg";
import TitleViewIcon from "../../../assets/images/kanBanIcon.svg";
import InfoIcon from "../../../assets/images/info-circle.svg";
import FilterIcon from "../../../assets/images/filterIcon.svg";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
  CommonButtonTexts,
} from "../../../enums/ButtonAttributesEnums";
import InputField from "../../../reusable-components/TNInputField";
import TNButton from "../../../reusable-components/TNButton";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import {
  ButtonGroupStyles,
  DOWNLOAD_DROPDOWN_CONFIG,
  DOWNLOAD_FORMATS,
  DOWNLOAD_OPTIONS,
  DOWNLOAD_OPTION_VALUES,
  fieldNames,
  utilitBarConstants,
  FilterButtonStyles,
  getConfirmMessage,
  getWarningMessage,
  FILTER_FIELD_NAMES_TO_COUNT,
} from "./constants";
import { disableFutureDates } from "../../../utils/disabledDateUtil";
import { useFormContext } from "react-hook-form";
import { ViewListingTypes } from "../../../enums/ViewListingTypes";
import clsx from "clsx";
import { DateFormat } from "../../../enums/dateTimeFormat";
import { TriggerType } from "../../../enums/TriggerTypeEnums";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import { CgSortAz } from "react-icons/cg";
import FilterDrawer from "./FilterDrawer";
import SocketLoader from "../../../reusable-components/SocketLoader";
import { socketActionType } from "../../../enums/socketEnums";
import { message, Modal } from "antd";
import InvoiceManagementServices from "../../../services/invoiceManagement.service";
import { filterFieldConstants } from "./FilterDrawer/FilterForm/constants";
import { SEARCH_FIELD_NAME } from "../TileViewTable/constants";
import SuccessModal from "../../../reusable-components/SuccessModal";
import {
  GenerationStatus,
  QuickbookStatus,
  UpdateInvoiceStatusKey,
} from "../../../enums/InvoiceManagementEnums";
import { showInvoiceNotification } from "../constants";
import { InvoiceProgressType } from "../../../Types/invoiceManagementTypes";
import { TNDropDown } from "../../../reusable-components/TNDropDown";
import { DropDownPosition } from "../../../enums/DropDownAttributesEnums";
import TNLoader from "../../../reusable-components/TNLoader";
import { countFiltersApplied } from "../../../utils/countAppliedFilters";

interface UtilityBarProps {
  handleSearch?: (val?: string) => void;
  setSearchState?: (val?: string) => void;
  searchInputValue?: string;
  handleViewToggle?: (view: ViewListingTypes) => void;
  activeView?: ViewListingTypes.KANBAN | ViewListingTypes.LIST;
  dateRanges: {};
  handleSetDateRange?: React.Dispatch<React.SetStateAction<{}>>;
  handleSetFilter?: () => void;
  currentFilter?: object;
  selectedKeyList: [];
  totalRecords: number;
  unselectedKeysLength: number;
  selectAllValue: boolean;
  editAccess?: boolean;
  setBulkUpdateKey?: React.Dispatch<React.SetStateAction<number>>;
  invoiceGenerationStatus?: GenerationStatus;
  generationData?: InvoiceProgressType;
  refetchAction?: () => void;
}

const UtilityBar = (props: UtilityBarProps) => {
  const {
    handleSearch,
    setSearchState,
    searchInputValue,
    handleViewToggle,
    activeView,
    dateRanges,
    handleSetDateRange,
    handleSetFilter,
    currentFilter,
    selectedKeyList = [],
    totalRecords,
    unselectedKeysLength,
    selectAllValue = false,
    editAccess = true,
    setBulkUpdateKey,
    invoiceGenerationStatus,
    generationData,
    refetchAction,
  } = props;
  const { KANBAN, LIST } = ViewListingTypes;
  const { watch, setValue, getValues } = useFormContext();

  const currentSearchValue = watch(fieldNames.SEARCH_NAME);

  const {
    getDownloadSuccess,
    EXPORT_SUCCESS,
    BUTTONS: { FILTER_LABEL, DOWNLOAD_LABEL, EXPORT_LABEL },
  } = utilitBarConstants;

  const {
    ExportSelectedList,
    GetListDownload,
    GetListCarrierDownload,
    BulkStatusUpdate,
    GetQuickBookSyncStatus,
  } = InvoiceManagementServices();

  const payload = selectAllValue
    ? {
        invoiceIds: selectedKeyList,
        selectAll: selectAllValue,
        companyId: getValues(filterFieldConstants.CUSTOMER_ID_FILTER_NAME),
        jobId: getValues(filterFieldConstants.JOB_ID_FILTER_NAME),
        orderId: getValues(filterFieldConstants.ORDER_ID_FILTER_NAME),
        invoiceId: getValues(filterFieldConstants.INVOICE_ID_FILTER_NAME),
        invoiceStatus: getValues(filterFieldConstants.STATUS_FILTER_NAME),
        syncStatus: getValues(
          filterFieldConstants.QUICKBOOK_STATUS_FILTER_NAME,
        ),
        search: getValues(SEARCH_FIELD_NAME),
      }
    : {
        invoiceIds: selectedKeyList,
        selectAll: selectAllValue,
      };

  const selectedCount = selectAllValue
    ? !unselectedKeysLength
      ? totalRecords
      : totalRecords - unselectedKeysLength
    : selectedKeyList?.length;

  const filterCount = countFiltersApplied(
    currentFilter,
    FILTER_FIELD_NAMES_TO_COUNT,
  );

  const { mutateAsync: listDownloadAsync } = GetListDownload();
  const { mutateAsync: listDownloadCarrierAsync } = GetListCarrierDownload();
  const { mutateAsync: exportCallAsync } = ExportSelectedList();
  const { mutateAsync: bulkSyncStatusUpdate } = BulkStatusUpdate();
  const {
    mutateAsync: getQuickBookSyncStatus,
    isPending: getQuickBookSyncStatusPending,
  } = GetQuickBookSyncStatus(payload);

  const utilityEnabled = selectAllValue
    ? false
    : !(selectedKeyList?.length > 0);
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [isExportConfirmOpen, setIsExportConfirmOpen] = useState(false);
  const [isDownloadConfirmOpen, setIsDownloadConfirmOpen] = useState(false);

  const [startDownloadAction, setStartDownloadAction] =
    useState<boolean>(false);
  const [startExportAction, setStartExportAction] = useState<boolean>(false);

  const [isBulkSyncUpdateModalOpen, setBulkSyncUpdateModalOpen] =
    useState<boolean>(false);
  const [exportSuccessOpen, setExportSuccessOpen] = useState<boolean>(false);
  const [downloadSuccessOpen, setDownloadSuccessOpen] =
    useState<boolean>(false);
  const [downloadOption, setDownloadOption] = useState<string | null>(null);
  const [nonSyncCount, setNonSyncCount] = useState<number | null>(null);
  const [clearAllFilterButton, setClearAllFilterButton] =
    useState<boolean>(false);

  const handleClearAllFilter = () => {
    handleSetFilter({});
    handleSetDateRange({});
    setValue(filterFieldConstants.FILTER_DATE_RANGE_FILTER_NAME, [
      undefined,
      undefined,
    ]);
    setValue(filterFieldConstants.INVOICE_ID_FILTER_NAME, undefined);
    setValue(filterFieldConstants.CUSTOMER_ID_FILTER_NAME, undefined);
    setValue(filterFieldConstants.JOB_ID_FILTER_NAME, undefined);
    setValue(filterFieldConstants.ORDER_ID_FILTER_NAME, undefined);
    setValue(filterFieldConstants.STATUS_FILTER_NAME, undefined);
    setValue(filterFieldConstants.QUICKBOOK_STATUS_FILTER_NAME, undefined);
    handleEnableClearAllButton();
  };

  const handleEnableClearAllButton = () => setClearAllFilterButton(true);

  const handleDisableClearAllButton = () => setClearAllFilterButton(false);

  const handleExportSuccessOpen = () => setExportSuccessOpen(true);
  const handleExportSuccessClose = () => setExportSuccessOpen(false);
  const handleDownloadSuccessOpen = () => setDownloadSuccessOpen(true);
  const handleDownloadSuccessClose = () => setDownloadSuccessOpen(false);

  const handleBulkSyncUpdateModalOpen = () => setBulkSyncUpdateModalOpen(true);
  const handleBulkSyncUpdateModalClose = () =>
    setBulkSyncUpdateModalOpen(false);

  const openFilterDrawer = () => setFilterDrawerOpen(true);
  const closeFilterDrawer = () => setFilterDrawerOpen(false);

  const handleExportConfirmOpen = () => setIsExportConfirmOpen(true);
  const handleExportConfirmClose = () => setIsExportConfirmOpen(false);
  const handleDownloadConfirmOpen = () => setIsDownloadConfirmOpen(true);
  const handleDownloadConfirmClose = () => setIsDownloadConfirmOpen(false);

  const handleExportActionEnd = () => {
    setStartExportAction(false);
  };
  const handleExportActionStart = () => {
    handleExportConfirmClose();
    setStartExportAction(true);
  };
  const handleDownloadActionEnd = () => {
    setStartDownloadAction(false);
    handleDownloadSuccessOpen();
  };
  const handleDownloadActionStart = () => {
    handleDownloadConfirmClose();
    setStartDownloadAction(true);
  };

  const handleDownloadOption = async (option) => {
    setDownloadOption(option);
    if (option !== DOWNLOAD_OPTION_VALUES.CARRIER_INVOICE_PDF) {
      await getQuickBookSyncStatus(payload, {
        onSuccess: (data) => {
          setNonSyncCount(data?.nonSyncedCount);
        },
        onError: (err) => {
          message?.error(err?.response?.message ?? "");
        },
      });
    }
    handleDownloadConfirmOpen();
  };

  const getModalContent = () => {
    if (downloadOption === DOWNLOAD_OPTION_VALUES.CARRIER_INVOICE_PDF) {
      return getConfirmMessage(
        selectedCount,
        socketActionType.DOWNLOAD_INVOICE,
      );
    }

    if (nonSyncCount === null) return null;

    return nonSyncCount === 0
      ? getConfirmMessage(selectedCount, socketActionType.DOWNLOAD_INVOICE)
      : getWarningMessage(nonSyncCount);
  };

  const dropDownItems = DOWNLOAD_OPTIONS.map(({ label, value }) => ({
    label,
    onClick: () => handleDownloadOption(value),
  }));

  const handleBulkSyncStatusUpdate = async () => {
    const payload = {
      filters: {
        invoiceIds: selectedKeyList,
        selectAll: selectAllValue,
        companyId: getValues(filterFieldConstants.CUSTOMER_ID_FILTER_NAME),
        jobId: getValues(filterFieldConstants.JOB_ID_FILTER_NAME),
        orderId: getValues(filterFieldConstants.ORDER_ID_FILTER_NAME),
        invoiceId: getValues(filterFieldConstants.INVOICE_ID_FILTER_NAME),
        invoiceStatus: getValues(filterFieldConstants.STATUS_FILTER_NAME),
        syncStatus: getValues(
          filterFieldConstants.QUICKBOOK_STATUS_FILTER_NAME,
        ),
      },
      statusFields: {
        status: QuickbookStatus.SYNCED,
        type: UpdateInvoiceStatusKey.SYNC_STATUS,
        fromExport: true,
      },
    };
    await bulkSyncStatusUpdate(payload, {
      onSuccess: () => {
        handleBulkSyncUpdateModalClose();
        setBulkUpdateKey((prev) => prev + 1);
      },
      onError: () => {
        message.error(utilitBarConstants.BULK_SYNC_FAILURE_MESSAGE);
        handleBulkSyncUpdateModalClose();
      },
    });
  };

  const exportActionCall = async () => {
    const response = await exportCallAsync(payload, {
      onSuccess: (success) => {
        return success;
      },
      onError: (Error) => {
        return Error?.response;
      },
    });
    return response;
  };

  const downloadActionCall = async () => {
    try {
      if (downloadOption === DOWNLOAD_OPTION_VALUES.CARRIER_INVOICE_PDF) {
        return await listDownloadAsync(payload);
      } else if (downloadOption === DOWNLOAD_OPTION_VALUES.TRUCK_INVOICE_PDF) {
        return await listDownloadCarrierAsync(payload);
      } else if (
        downloadOption === DOWNLOAD_OPTION_VALUES.TRUCK_INVOICE_EXCEL
      ) {
        return await listDownloadCarrierAsync({
          ...payload,
          format: DOWNLOAD_FORMATS.EXCEL,
        });
      }
    } catch (error) {
      return error;
    }
  };

  const filterLabel = (
    <div className={styles.filterLabel}>
      <span>{FILTER_LABEL}</span> <CgSortAz className={styles.SortIcon} />
    </div>
  );

  const addOnAfter = (
    <TNButton
      shape={ButtonShape.DEFAULT}
      size={ButtonSize.SMALL}
      className={styles.searchFieldButton}
      block
      text={
        <img
          className={styles.searchFieldIcon}
          src={SearchIcon}
          alt={utilitBarConstants.SEARCH_ICON_ALT}
        />
      }
      onClick={() => handleSearch?.(currentSearchValue)}
    />
  );

  useEffect(() => {
    setSearchState?.(currentSearchValue);
  }, [currentSearchValue]);

  useEffect(() => {
    setValue(fieldNames.DATE_RANGE, [
      dateRanges?.startDate,
      dateRanges?.endDate,
    ]);
  }, [dateRanges]);

  return (
    <>
      <TNLoader isLoading={getQuickBookSyncStatusPending} />
      <SuccessModal
        message={EXPORT_SUCCESS}
        isWarningModalOpen={exportSuccessOpen}
        closeHandler={handleExportSuccessClose}
      />
      <SuccessModal
        formattedMessage={getDownloadSuccess(
          selectAllValue
            ? !unselectedKeysLength
              ? totalRecords
              : totalRecords - unselectedKeysLength
            : selectedKeyList?.length,
        )}
        isWarningModalOpen={downloadSuccessOpen}
        closeHandler={handleDownloadSuccessClose}
      />
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        closeDrawer={closeFilterDrawer}
        handleSetFilter={handleSetFilter}
        handleSetDateRange={handleSetDateRange}
        currentFilter={currentFilter}
        clearAllFilterButton={clearAllFilterButton}
        handleDisableClearAllFilterButton={handleDisableClearAllButton}
        handleClearAllFilter={handleClearAllFilter}
      />
      <Modal
        title={utilitBarConstants.BULK_SYNC_UPDATE_TITLE}
        width={utilitBarConstants.MODAL_WIDTH}
        visible={isBulkSyncUpdateModalOpen}
        okButtonProps={{
          title: utilitBarConstants.BUTTONS.CONFIRM_LABEL,
        }}
        onOk={handleBulkSyncStatusUpdate}
        onCancel={handleBulkSyncUpdateModalClose}
        maskClosable={false}
      >
        {utilitBarConstants.BULK_SYNC_STATUS_CONTENT}
      </Modal>
      <Modal
        title={utilitBarConstants.DOWNLOAD_TILE}
        width={utilitBarConstants.MODAL_WIDTH}
        visible={isDownloadConfirmOpen}
        onCancel={handleDownloadConfirmClose}
        onOk={handleDownloadActionStart}
        okText={CommonButtonTexts.CONFIRM}
      >
        {getModalContent()}
      </Modal>
      <Modal
        title={utilitBarConstants.EXPORT_TITLE}
        width={utilitBarConstants.MODAL_WIDTH}
        visible={isExportConfirmOpen}
        onCancel={handleExportConfirmClose}
        onOk={handleExportActionStart}
        okText={CommonButtonTexts.CONFIRM}
      >
        {getConfirmMessage(
          selectAllValue
            ? !unselectedKeysLength
              ? totalRecords
              : totalRecords - unselectedKeysLength
            : selectedKeyList?.length,
          socketActionType.EXPORT_TO_QUICKBOOKS,
        )}
      </Modal>
      <ConditionalRender visible={startDownloadAction}>
        <SocketLoader
          actionType={socketActionType.DOWNLOAD_INVOICE}
          handleActionCall={downloadActionCall}
          closeRender={handleDownloadActionEnd}
        />
      </ConditionalRender>
      <ConditionalRender visible={startExportAction}>
        <SocketLoader
          actionType={socketActionType.EXPORT_TO_QUICKBOOKS}
          handleActionCall={exportActionCall}
          closeRender={handleExportActionEnd}
          successCallback={() => {
            handleExportSuccessOpen();
            handleBulkSyncUpdateModalOpen();
          }}
        />
      </ConditionalRender>
      <div className={styles.headerSection}>
        <div className={styles.leftContainer}>
          <div className={styles.invoiceSearch}>
            <InputField
              type={InputfieldTypes.TEXT}
              name={fieldNames.SEARCH_NAME}
              placeholder={utilitBarConstants.SEARCH_PLACEHOLDER}
              addonAfter={addOnAfter}
              value={searchInputValue}
              onKeyDown={(e) => {
                if (e.key === TriggerType.ENTER) {
                  e.preventDefault();
                  handleSearch?.(currentSearchValue);
                }
              }}
            />
          </div>
          <InputField
            type={InputfieldTypes.RANGE_PICKER}
            suffix={
              <img src={CalendarIcon} alt={utilitBarConstants.CALENDAR_ALT} />
            }
            disabledDates={disableFutureDates}
            name={fieldNames.DATE_RANGE}
            allowDateClear
            placeholder={utilitBarConstants.DATE_PLACEHOLDER}
            onRangePickerChange={(dates) => {
              if (!dates || dates.length === 0) {
                setValue(fieldNames.DATE_RANGE, null);
                handleSetDateRange?.({
                  startDate: undefined,
                  endDate: undefined,
                });
              } else {
                const start = dates[0].format(DateFormat.MM_DD_YYYY_SLASHED);
                const end = dates[1].format(DateFormat.MM_DD_YYYY_SLASHED);
                setValue(fieldNames.DATE_RANGE, [start, end]);
                handleSetDateRange?.({ startDate: start, endDate: end });
              }
            }}
          />
          <ConditionalRender visible={activeView === LIST}>
            <div className={styles.filterContainer}>
              <TNButton
                filterButton
                shape={ButtonShape.DEFAULT}
                size={ButtonSize.LARGE}
                badgeCount={filterCount}
                text="Filter"
                className={styles.filterButton}
                afterTitle={true}
                icon={<img src={FilterIcon} />}
                onClick={openFilterDrawer}
              />
              {filterCount > 0 && (
                <TNButton
                  clearFilterButton
                  shape={ButtonShape.ROUND}
                  size={ButtonSize.MEDIUM}
                  text="Clear All"
                  onClick={handleClearAllFilter}
                />
              )}
            </div>
          </ConditionalRender>
        </div>
        <div className={styles.rightContainer}>
          <div className={styles.DownloadCTA}>
            <ConditionalRender
              visible={invoiceGenerationStatus === GenerationStatus.IN_PROGRESS}
            >
              <TNButton
                type={ButtonType.DEFAULT}
                size={ButtonSize.LARGE}
                className={styles.infoIconButton}
                shape={ButtonShape.DEFAULT}
                text={
                  <span className={styles.infoIcon}>
                    <img
                      src={InfoIcon}
                      alt={utilitBarConstants.INFO_ICON_ALT}
                    />
                  </span>
                }
                onClick={() => {
                  showInvoiceNotification(generationData, refetchAction);
                }}
              />
            </ConditionalRender>
            <ConditionalRender visible={activeView === LIST}>
              <TNButton
                shape={ButtonShape.DEFAULT}
                size={ButtonSize.LARGE}
                type={ButtonType.DEFAULT}
                onClick={() => handleExportConfirmOpen()}
                text={EXPORT_LABEL}
                disabled={!editAccess || utilityEnabled}
              />
              <TNDropDown
                type={ButtonType.PRIMARY}
                menuItems={dropDownItems}
                position={DropDownPosition.BOTTOM}
                text={DOWNLOAD_LABEL}
                disabled={!editAccess || utilityEnabled}
              />
            </ConditionalRender>
          </div>
          <div className={styles.innerViewWrapper}>
            <TNButton
              customStyles={ButtonGroupStyles}
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              text={
                <div
                  className={clsx(styles.hoverButtonGrp, {
                    [styles.selected]: activeView === LIST,
                  })}
                >
                  <img
                    src={ListViewIcon}
                    alt={utilitBarConstants.LIST_ICON_ALT}
                  />
                </div>
              }
              onClick={() => handleViewToggle?.(LIST)}
            />
            <TNButton
              customStyles={ButtonGroupStyles}
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              text={
                <div
                  className={clsx(styles.hoverButtonGrp, {
                    [styles.selected]: activeView === KANBAN,
                  })}
                >
                  <img
                    src={TitleViewIcon}
                    alt={utilitBarConstants.KANBAN_ICON_ALT}
                  />
                </div>
              }
              onClick={() => handleViewToggle?.(KANBAN)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default UtilityBar;
